import { Plus, Trash2 } from "lucide-react";
import { ROTULO_DO_ELEMENTO, TIPOS_DE_ELEMENTO, type ElementoDaPlanta, type TipoDeElemento } from "@bookdental/schemas";
import type { SalaAgora } from "../use-salas-agora";
import type { Selecao } from "./use-editor-da-planta";
import { TamanhoDaPlanta, type TamanhoDaPlantaProps } from "./TamanhoDaPlanta";

type Props = {
  tamanho: TamanhoDaPlantaProps;
  foraDaPlanta: SalaAgora[];
  selecionado: { selecao: NonNullable<Selecao>; nome: string; elemento?: ElementoDaPlanta } | null;
  onAdicionarSala: (id: string) => void;
  onAdicionarElemento: (tipo: TipoDeElemento) => void;
  onRenomear: (id: string, rotulo: string) => void;
  onRemover: (s: NonNullable<Selecao>) => void;
};

const item =
  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-zinc-700 hover:bg-zinc-100 dark:text-foreground dark:hover:bg-muted";
const titulo = "px-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground";

/** Coluna do editor: o que falta pôr na planta, os elementos e a peça selecionada. */
export function PaletaDaPlanta({ tamanho, foraDaPlanta, selecionado, onAdicionarSala, onAdicionarElemento, onRenomear, onRemover }: Props) {
  return (
    <aside className="space-y-5 rounded-xl border border-zinc-200 bg-white p-3 dark:border-border dark:bg-card">
      <section className="space-y-1">
        <p className={titulo}>Salas fora da planta</p>
        {foraDaPlanta.length ? (
          foraDaPlanta.map((s) => (
            <button key={s.id} type="button" className={item} onClick={() => onAdicionarSala(s.id)}>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.cor ?? "#a1a1aa" }} />
              <span className="flex-1 truncate">{s.nome}</span>
              <Plus className="h-3.5 w-3.5 text-zinc-400" />
            </button>
          ))
        ) : (
          <p className="px-2 text-xs text-zinc-500 dark:text-muted-foreground">Todas as salas já estão na planta.</p>
        )}
      </section>

      <section className="space-y-1">
        <p className={titulo}>Outros espaços</p>
        {TIPOS_DE_ELEMENTO.map((t) => (
          <button key={t} type="button" className={item} onClick={() => onAdicionarElemento(t)}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm border border-dashed border-zinc-400" />
            <span className="flex-1">{ROTULO_DO_ELEMENTO[t]}</span>
            <Plus className="h-3.5 w-3.5 text-zinc-400" />
          </button>
        ))}
      </section>

      <section className="space-y-1.5">
        <p className={titulo}>Tamanho da planta</p>
        <TamanhoDaPlanta {...tamanho} />
      </section>

      {selecionado && (
        <section className="space-y-2 border-t border-zinc-100 pt-3 dark:border-border">
          <p className={titulo}>Selecionado</p>
          {selecionado.elemento ? (
            <input
              aria-label="Nome do espaço"
              value={selecionado.elemento.rotulo ?? ""}
              placeholder={ROTULO_DO_ELEMENTO[selecionado.elemento.tipo]}
              maxLength={40}
              onChange={(e) => onRenomear(selecionado.elemento!.id, e.target.value)}
              className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm dark:border-border dark:bg-card"
            />
          ) : (
            <p className="px-2 text-sm font-medium text-zinc-900 dark:text-foreground">{selecionado.nome}</p>
          )}
          <button type="button" className={`${item} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10`} onClick={() => onRemover(selecionado.selecao)}>
            <Trash2 className="h-3.5 w-3.5" />
            Tirar da planta
          </button>
          <p className="px-2 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">Setas movem; Shift + setas mudam o tamanho.</p>
        </section>
      )}
    </aside>
  );
}
